import type { SupabaseClient } from '@supabase/supabase-js';
import {
  getCapturesToSync,
  updateLocalCapture,
  type LocalCapture,
} from './local-db';

export interface SyncReport {
  synced: number;
  failed: number;
}

let activeSync: Promise<SyncReport> | null = null;

function compactDeviceName() {
  if (/Android/i.test(navigator.userAgent)) return 'Android PWA';
  if (/iPhone|iPad/i.test(navigator.userAgent)) return 'iOS PWA';
  return 'Web';
}

async function syncCapture(client: SupabaseClient, capture: LocalCapture) {
  await updateLocalCapture(capture.id, { syncStatus: 'syncing', syncError: undefined });

  const { error } = await client.from('entries').upsert(
    {
      id: capture.id,
      raw_text: capture.rawText,
      captured_at: capture.capturedAt,
      source: 'pwa',
      device_name: compactDeviceName(),
    },
    { onConflict: 'id', ignoreDuplicates: true },
  );

  if (error) {
    await updateLocalCapture(capture.id, { syncStatus: 'failed', syncError: error.message });
    throw error;
  }

  await updateLocalCapture(capture.id, {
    syncStatus: 'synced',
    syncError: undefined,
    syncedAt: new Date().toISOString(),
  });

  return capture.id;
}

async function requestAnalysis(entryId: string, accessToken: string) {
  const response = await fetch('/api/analyze', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ entryId }),
  });
  if (!response.ok) throw new Error(`AI analysis failed (${response.status})`);
}

async function performSync(client: SupabaseClient): Promise<SyncReport> {
  if (!navigator.onLine) return { synced: 0, failed: 0 };

  const session = (await client.auth.getSession()).data.session;
  if (!session) return { synced: 0, failed: 0 };

  const captures = await getCapturesToSync(session.user.id);
  let synced = 0;
  let failed = 0;

  for (const capture of captures) {
    try {
      const id = await syncCapture(client, capture);
      synced += 1;
      void requestAnalysis(id, session.access_token).catch(() => {
        // The server row remains pending and will be picked up on the next resume.
      });
    } catch {
      failed += 1;
    }
  }

  return { synced, failed };
}

export function syncPendingCaptures(client: SupabaseClient) {
  if (activeSync) return activeSync;
  activeSync = performSync(client).finally(() => {
    activeSync = null;
  });
  return activeSync;
}

export async function resumePendingAnalysis(client: SupabaseClient) {
  if (!navigator.onLine) return;
  const session = (await client.auth.getSession()).data.session;
  if (!session) return;

  const { data } = await client
    .from('entries')
    .select('id')
    .in('ai_status', ['pending', 'failed'])
    .lt('analysis_attempts', 3)
    .order('captured_at', { ascending: true })
    .limit(5);

  for (const entry of data || []) {
    try {
      await requestAnalysis(entry.id as string, session.access_token);
    } catch {
      break;
    }
  }
}
